import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Row, Col, Badge, Button, ButtonGroup } from 'react-bootstrap';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../config/api';

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d;
}

function getWeekDates(date) {
  const monday = getMonday(date);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(d);
  }
  return dates;
}

function calcHours(timeEntries) {
  if (!timeEntries || !Array.isArray(timeEntries)) return 0;
  let total = 0;
  for (const entry of timeEntries) {
    const [sh, sm] = entry.start.split(':').map(Number);
    const [eh, em] = entry.end.split(':').map(Number);
    total += (eh * 60 + em - sh * 60 - sm) / 60;
  }
  return Math.max(0, total);
}

/**
 * Inline work log form for logging own time
 */
function WorkLogForm({ apiCall, date, onSaved }) {
  const [timeEntries, setTimeEntries] = useState([]);
  const [workAccomplished, setWorkAccomplished] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadLog = async (dateStr) => {
    try {
      setLoading(true);
      const response = await apiCall(`${API_BASE_URL}/work-logs?date=${dateStr}&own=1`);
      const logs = response.data || [];
      if (logs.length > 0) {
        setTimeEntries(logs[0].time_entries || []);
        setWorkAccomplished(logs[0].work_accomplished || '');
      } else {
        setTimeEntries([]);
        setWorkAccomplished('');
      }
    } catch (error) {
      console.error('Failed to load work log:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLog(date);
    setSaved(false);
  }, [date]);

  const addTimeEntry = () => {
    setTimeEntries([...timeEntries, { start: '09:00', end: '17:00' }]);
    setSaved(false);
  };

  const removeTimeEntry = (index) => {
    setTimeEntries(timeEntries.filter((_, i) => i !== index));
    setSaved(false);
  };

  const updateTimeEntry = (index, field, value) => {
    const updated = [...timeEntries];
    updated[index] = { ...updated[index], [field]: value };
    setTimeEntries(updated);
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiCall(`${API_BASE_URL}/work-logs`, {
        method: 'POST',
        body: JSON.stringify({
          log_date: date,
          time_entries: timeEntries,
          work_accomplished: workAccomplished,
        }),
      });
      setSaved(true);
      if (onSaved) onSaved();
    } catch (error) {
      console.error('Failed to save work log:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-4">
      <Card.Header className="text-white d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f97316' }}>
        <div>
          <i className="fas fa-clock me-2"></i>
          My Work Log
        </div>
        {timeEntries.length > 0 && (
          <Badge bg="light" text="dark">{calcHours(timeEntries).toFixed(1)} hrs</Badge>
        )}
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-center py-2">
            <div className="spinner-border spinner-border-sm" role="status" />
          </div>
        ) : (
          <>
            {timeEntries.map((entry, i) => (
              <div key={i} className="d-flex align-items-center gap-2 mb-2">
                <Form.Control
                  type="time"
                  size="sm"
                  value={entry.start}
                  onChange={(e) => updateTimeEntry(i, 'start', e.target.value)}
                  style={{ width: 120 }}
                />
                <span>-</span>
                <Form.Control
                  type="time"
                  size="sm"
                  value={entry.end}
                  onChange={(e) => updateTimeEntry(i, 'end', e.target.value)}
                  style={{ width: 120 }}
                />
                <Button variant="outline-danger" size="sm" onClick={() => removeTimeEntry(i)}>
                  &times;
                </Button>
              </div>
            ))}

            <Button variant="outline-secondary" size="sm" onClick={addTimeEntry} className="mb-3">
              + Add Time Block
            </Button>

            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Work accomplished..."
              value={workAccomplished}
              onChange={(e) => { setWorkAccomplished(e.target.value); setSaved(false); }}
              className="mb-3"
              size="sm"
            />

            <Button
              variant={saved ? 'success' : 'primary'}
              size="sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : saved ? 'Saved' : 'Save'}
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

/**
 * Agent view: see own work logs for a selected date, with inline editing
 */
function AgentCalendarView() {
  const { apiCall } = useAuth();
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">Work Calendar</h2>

      <Form.Group className="mb-4" style={{ maxWidth: 250 }}>
        <Form.Label>Select Date</Form.Label>
        <Form.Control
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
      </Form.Group>

      <WorkLogForm apiCall={apiCall} date={selectedDate} />
    </Container>
  );
}

/**
 * Admin/Manager view: see all agents' logs with day/week toggle
 */
function AdminCalendarView() {
  const { apiCall, user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [viewMode, setViewMode] = useState('day'); // 'day' or 'week'
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [storeFilter, setStoreFilter] = useState('');
  const [stores, setStores] = useState([]);
  const [deleting, setDeleting] = useState(null);

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this work log?')) return;
    try {
      setDeleting(logId);
      await apiCall(`${API_BASE_URL}/work-logs/${logId}`, { method: 'DELETE' });
      await loadLogs();
    } catch (error) {
      console.error('Failed to delete work log:', error);
    } finally {
      setDeleting(null);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      loadStores();
    }
  }, [user]);

  const loadStores = async () => {
    try {
      const response = await apiCall(`${API_BASE_URL}/stores`);
      setStores(response.data || []);
    } catch (error) {
      console.error('Failed to load stores:', error);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      let url;
      if (viewMode === 'day') {
        url = `${API_BASE_URL}/work-logs?date=${selectedDate}`;
      } else {
        const weekDates = getWeekDates(new Date(selectedDate + 'T00:00:00'));
        const startDate = formatDate(weekDates[0]);
        const endDate = formatDate(weekDates[6]);
        url = `${API_BASE_URL}/work-logs?start_date=${startDate}&end_date=${endDate}`;
      }
      if (storeFilter) {
        url += `&store_id=${storeFilter}`;
      }
      const response = await apiCall(url);
      setLogs(response.data || []);
    } catch (error) {
      console.error('Failed to load work logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [selectedDate, viewMode, storeFilter]);

  // Group logs by date for week view
  const logsByDate = {};
  logs.forEach((log) => {
    const date = log.log_date?.split('T')[0] || log.log_date;
    if (!logsByDate[date]) logsByDate[date] = [];
    logsByDate[date].push(log);
  });

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">Work Calendar</h2>

      <Row className="mb-4 align-items-end">
        <Col xs="auto">
          <Form.Group>
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col xs="auto">
          <ButtonGroup>
            <Button
              variant={viewMode === 'day' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('day')}
            >
              Day
            </Button>
            <Button
              variant={viewMode === 'week' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('week')}
            >
              Week
            </Button>
          </ButtonGroup>
        </Col>
        {user?.role === 'admin' && stores.length > 0 && (
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Store</Form.Label>
              <Form.Select
                size="sm"
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
              >
                <option value="">All Stores</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        )}
      </Row>

      <WorkLogForm apiCall={apiCall} date={selectedDate} onSaved={loadLogs} />

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border" role="status" />
        </div>
      ) : viewMode === 'day' ? (
        // Day view
        logs.length > 0 ? (
          logs.map((log) => (
            <Card key={log.id} className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="mb-1">{log.agent_name}</h6>
                    {log.store_name && <small className="text-muted">{log.store_name}</small>}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg="primary">{calcHours(log.time_entries).toFixed(1)} hrs</Badge>
                    {user?.role === 'admin' && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteLog(log.id)}
                        disabled={deleting === log.id}
                      >
                        {deleting === log.id ? '...' : '×'}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2">
                  {(log.time_entries || []).map((entry, i) => (
                    <Badge key={i} bg="light" text="dark" className="me-1">{entry.start}-{entry.end}</Badge>
                  ))}
                </div>
                {log.work_accomplished && (
                  <p className="mt-2 mb-0 text-muted" style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
                    {log.work_accomplished}
                  </p>
                )}
              </Card.Body>
            </Card>
          ))
        ) : (
          <Card><Card.Body className="text-muted text-center py-4">No work logs for this date.</Card.Body></Card>
        )
      ) : (
        // Week view
        getWeekDates(new Date(selectedDate + 'T00:00:00')).map((date) => {
          const dateStr = formatDate(date);
          const dayLogs = logsByDate[dateStr] || [];
          const dayName = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

          return (
            <Card key={dateStr} className="mb-3">
              <Card.Header className="py-2">
                <strong>{dayName}</strong>
                {dayLogs.length > 0 && (
                  <Badge bg="secondary" className="ms-2">
                    {dayLogs.length} log{dayLogs.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </Card.Header>
              {dayLogs.length > 0 ? (
                <Card.Body className="py-2">
                  {dayLogs.map((log) => (
                    <div key={log.id} className="py-2 border-bottom">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <span className="fw-medium">{log.agent_name}</span>
                          {log.store_name && <small className="text-muted ms-2">{log.store_name}</small>}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg="primary">{calcHours(log.time_entries).toFixed(1)} hrs</Badge>
                          {user?.role === 'admin' && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              style={{ padding: '0 4px', fontSize: '0.75rem', lineHeight: 1.2 }}
                              onClick={() => handleDeleteLog(log.id)}
                              disabled={deleting === log.id}
                            >
                              {deleting === log.id ? '...' : '×'}
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="mt-1">
                        {(log.time_entries || []).map((entry, i) => (
                          <Badge key={i} bg="light" text="dark" className="me-1">{entry.start}-{entry.end}</Badge>
                        ))}
                      </div>
                      {log.work_accomplished && (
                        <p className="mt-1 mb-0 text-muted" style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                          {log.work_accomplished}
                        </p>
                      )}
                    </div>
                  ))}
                </Card.Body>
              ) : (
                <Card.Body className="py-2 text-muted" style={{ fontSize: '0.85rem' }}>No logs</Card.Body>
              )}
            </Card>
          );
        })
      )}
    </Container>
  );
}

/**
 * WorkCalendar - renders admin or agent view based on role
 */
export default function WorkCalendar() {
  const { user } = useAuth();

  if (user?.role === 'admin' || user?.role === 'manager') {
    return <AdminCalendarView />;
  }

  return <AgentCalendarView />;
}
